import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield, Lock, Database, Cookie, Mail, AlertCircle, FileCheck, Users } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";

const SITE_URL = "https://www.mostrandomexico.mx";

export const Route = createFileRoute("/confianza")({
  head: () => ({
    meta: [
      { title: "Centro de Confianza — Mostrando México" },
      {
        name: "description",
        content:
          "Cómo Mostrando México protege tus datos, gestiona pagos, reservas y privacidad. Página mantenida por Mostrando México.",
      },
      { property: "og:title", content: "Centro de Confianza — Mostrando México" },
      {
        property: "og:description",
        content:
          "Información de seguridad, privacidad y prácticas operativas mantenida por Mostrando México.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/confianza` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/confianza` }],
  }),
  component: TrustPage,
});

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        <h2 className="font-display text-xl font-semibold">{title}</h2>
      </div>
      <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground/85">
        {children}
      </div>
    </section>
  );
}

function TrustPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:py-16">
        <header className="mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Shield className="h-3.5 w-3.5" /> Centro de Confianza
          </div>
          <h1 className="mt-4 font-display text-4xl font-semibold sm:text-5xl">
            Seguridad, privacidad y operación
          </h1>
          <p className="mt-4 text-base text-muted-foreground">
            Esta página es mantenida por Mostrando México para responder las
            preguntas más comunes sobre cómo cuidamos tu información cuando
            reservas tours, pagos y experiencias en nuestra plataforma. No es
            una certificación independiente ni una verificación externa: es
            contenido editable mantenido por el equipo del sitio.
          </p>
        </header>

        <div className="grid gap-5">
          <Section icon={Lock} title="Autenticación y accesos">
            <p>
              Las cuentas se crean con correo electrónico o con Google. Las
              sesiones se gestionan con tokens firmados; el sitio nunca
              almacena tu contraseña en texto plano.
            </p>
            <p>
              El panel de administración y los datos sensibles de reservas
              solo son accesibles a personal autorizado de Mostrando México
              mediante roles asignados explícitamente.
            </p>
          </Section>

          <Section icon={Database} title="Cómo se procesan las reservas">
            <p>
              Las reservas se crean exclusivamente desde nuestros servidores
              tras validar disponibilidad y precios contra el proveedor
              oficial (Bokun para tours, pasarelas autorizadas para pagos).
              Los clientes no pueden crear reservas directamente en la base
              de datos.
            </p>
            <p>
              Los precios mostrados en el carrito y el checkout se vuelven a
              verificar en el servidor antes de cobrar, para evitar
              manipulaciones.
            </p>
          </Section>

          <Section icon={FileCheck} title="Pagos">
            <p>
              Los pagos con tarjeta son procesados por proveedores
              especializados (por ejemplo Stripe). Mostrando México no
              almacena números completos de tarjeta ni CVV en su base de
              datos.
            </p>
            <p>
              Las notificaciones de pago se verifican con firma criptográfica
              antes de marcar una reserva como pagada.
            </p>
          </Section>

          <Section icon={Database} title="Datos que recopilamos">
            <p>
              Para completar una reserva guardamos los datos estrictamente
              necesarios: nombre, correo, teléfono de contacto, detalles del
              tour (fecha, pasajeros, punto de encuentro) y el estado de
              pago. Esta información se usa para confirmar y operar tu
              experiencia.
            </p>
            <p>
              Consulta detalles adicionales en nuestra{" "}
              <Link to="/privacidad" className="font-semibold text-primary hover:underline">
                Política de Privacidad
              </Link>{" "}
              y los{" "}
              <Link to="/terminos" className="font-semibold text-primary hover:underline">
                Términos y Condiciones
              </Link>
              .
            </p>
          </Section>

          <Section icon={Users} title="Subprocesadores e integraciones">
            <p>
              Para operar el servicio utilizamos proveedores especializados,
              entre otros: Bokun (catálogo y disponibilidad de tours), Stripe
              (pagos), proveedores de correo transaccional, y servicios de
              alojamiento y bases de datos en la nube. Cada uno recibe
              únicamente los datos que necesita para cumplir su función.
            </p>
          </Section>

          <Section icon={Cookie} title="Cookies y analítica">
            <p>
              Usamos cookies estrictamente necesarias para mantener tu
              sesión, recordar el idioma y el contenido del carrito. Podemos
              usar herramientas de analítica agregada para entender el uso
              del sitio sin identificarte personalmente.
            </p>
          </Section>

          <Section icon={AlertCircle} title="Retención y eliminación">
            <p>
              Conservamos los datos de reservas el tiempo necesario para
              cumplir obligaciones fiscales y de atención al cliente. Puedes
              solicitar la eliminación de tu cuenta y de los datos
              personales asociados escribiéndonos al correo de privacidad.
            </p>
          </Section>

          <Section icon={Mail} title="Contacto de seguridad y privacidad">
            <p>
              ¿Detectaste un problema de seguridad o quieres ejercer tus
              derechos de privacidad? Escríbenos a{" "}
              <a
                href="mailto:direccion@mostrandomexico.com"
                className="font-semibold text-primary hover:underline"
              >
                direccion@mostrandomexico.com
              </a>
              . Atendemos divulgaciones responsables y solicitudes de
              acceso, rectificación o eliminación.
            </p>
          </Section>
        </div>

        <p className="mt-10 text-xs text-muted-foreground">
          Última revisión por el equipo de Mostrando México. Esta página es
          contenido editable del sitio y puede actualizarse cuando cambien
          nuestras prácticas o integraciones.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
